import React, { useState } from "react";
import { Modal, Upload, Input, Button, Progress } from "antd";
import {
  InboxOutlined,
  CloseOutlined,
  FileOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ReloadOutlined,
} from "@ant-design/icons";
import styles from "./PieceJointeSingleForm.module.scss";
import Toast from "../../helpers/Toast";
import PieceJointeService from "../../services/PieceJointeService";
import { catchError } from "../../services/DaoService";
import PropTypes from "prop-types";

const { Dragger } = Upload;

// status: 'pending' | 'uploading' | 'success' | 'error'

const fileIcon = (name = "") => {
  const ext = name.split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext))
    return <FileImageOutlined style={{ color: "#7c3aed" }} />;
  if (ext === "pdf") return <FilePdfOutlined style={{ color: "#dc2626" }} />;
  if (["doc", "docx"].includes(ext))
    return <FileWordOutlined style={{ color: "#1d4ed8" }} />;
  if (["xls", "xlsx"].includes(ext))
    return <FileExcelOutlined style={{ color: "#16a34a" }} />;
  return <FileOutlined style={{ color: "#6b7280" }} />;
};

const nameWithoutExt = (filename = "") =>
  filename.split(".").slice(0, -1).join(".").replace(/[-_]/g, " ") || filename;

const PieceJointeUploadModal = ({
  visible,
  onClose,
  parentType,
  parentId,
  onUploadFinish,
}) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  React.useEffect(() => {
    if (!visible) setFileList([]);
  }, [visible]);

  const handleBeforeUpload = (file) => {
    if (file.size > 200 * 1024 * 1024) {
      Toast.error(`${file.name} dépasse la taille maximale de 200 Mo.`);
      return false;
    }
    setFileList((prev) => [
      ...prev,
      {
        uid: file.uid,
        file,
        displayName: nameWithoutExt(file.name),
        description: "",
        progress: 0,
        status: "pending", // pending | uploading | success | error
        errorMsg: null,
      },
    ]);
    return false;
  };

  const updateItem = (uid, patch) =>
    setFileList((prev) =>
      prev.map((item) => (item.uid === uid ? { ...item, ...patch } : item))
    );

  const removeItem = (uid) =>
    setFileList((prev) => prev.filter((item) => item.uid !== uid));

  const uploadItem = async (item) => {
    updateItem(item.uid, { status: "uploading", progress: 0, errorMsg: null });
    const formData = new FormData();
    formData.append("piece", item.file);
    formData.append("nom", item.displayName || item.file.name);
    formData.append("description", item.description || "");
    formData.append("parent_type", parentType);
    formData.append("parent_id", parentId);

    try {
      await PieceJointeService.getInstance().create(formData, (progress) =>
        updateItem(item.uid, { progress })
      );
      updateItem(item.uid, { status: "success", progress: 100 });
      onUploadFinish();
    } catch (err) {
      const msg =
        err?.response?.data?.message ?? "Erreur lors du téléversement.";
      updateItem(item.uid, { status: "error", progress: 0, errorMsg: msg });
    }
  };

  const handleUploadAll = async () => {
    const targets = fileList.filter((i) => i.status === "pending" || i.status === "error");
    if (targets.length === 0) {
      Toast.error("Aucun fichier à téléverser.");
      return;
    }
    setUploading(true);
    for (const item of targets) {
      await uploadItem(item);
    }
    setUploading(false);
  };

  const handleRetryFailed = async () => {
    const failed = fileList.filter((i) => i.status === "error");
    if (failed.length === 0) return;
    setUploading(true);
    for (const item of failed) {
      await uploadItem(item);
    }
    setUploading(false);
  };

  const pendingCount = fileList.filter((i) => i.status === "pending").length;
  const errorCount = fileList.filter((i) => i.status === "error").length;
  const successCount = fileList.filter((i) => i.status === "success").length;

  const allDone = fileList.length > 0 && pendingCount === 0 && errorCount === 0;

  const footerButtons = [];
  if (errorCount > 0 && !uploading) {
    footerButtons.push(
      <Button
        key="retry"
        icon={<ReloadOutlined />}
        danger
        onClick={handleRetryFailed}
      >
        Relancer les échecs ({errorCount})
      </Button>
    );
  }
  if (allDone) {
    footerButtons.push(
      <Button key="close" type="primary" onClick={onClose}>
        Fermer
      </Button>
    );
  } else {
    footerButtons.push(
      <Button
        key="upload"
        type="primary"
        loading={uploading}
        disabled={fileList.length === 0 || (pendingCount === 0 && errorCount === 0)}
        onClick={handleUploadAll}
      >
        {uploading
          ? "Téléversement..."
          : `Téléverser${pendingCount > 0 ? ` (${pendingCount})` : ""}`}
      </Button>
    );
  }

  return (
    <Modal
      title="Ajouter des documents"
      open={visible}
      onCancel={uploading ? undefined : onClose}
      closable={!uploading}
      maskClosable={!uploading}
      width={600}
      footer={footerButtons}
      destroyOnClose
    >
      <Dragger
        multiple
        beforeUpload={handleBeforeUpload}
        showUploadList={false}
        className={styles.dropZone}
        disabled={uploading}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Cliquez ou glissez des fichiers ici</p>
        <p className="ant-upload-hint">
          PDF, Word, Excel, images, vidéos… Max 200 Mo par fichier.
        </p>
      </Dragger>

      {fileList.length > 0 && (
        <div className={styles.fileList}>
          {successCount > 0 && (
            <div className={styles.summary}>
              <CheckCircleFilled style={{ color: "#16a34a" }} />
              {successCount} fichier{successCount > 1 ? "s" : ""} téléversé{successCount > 1 ? "s" : ""} avec succès
            </div>
          )}
          {fileList.map((item) => (
            <div
              key={item.uid}
              className={`${styles.fileItem} ${styles[`status_${item.status}`]}`}
            >
              <div className={styles.fileItemIcon}>
                {item.status === "success" ? (
                  <CheckCircleFilled style={{ color: "#16a34a", fontSize: 20 }} />
                ) : item.status === "error" ? (
                  <CloseCircleFilled style={{ color: "#dc2626", fontSize: 20 }} />
                ) : (
                  fileIcon(item.file.name)
                )}
              </div>
              <div className={styles.fileItemBody}>
                <div className={styles.fileItemOriginal}>{item.file.name}</div>
                {item.status === "pending" && (
                  <>
                    <Input
                      size="small"
                      placeholder="Nom affiché"
                      value={item.displayName}
                      onChange={(e) =>
                        updateItem(item.uid, { displayName: e.target.value })
                      }
                      disabled={uploading}
                      className={styles.fileItemInput}
                    />
                    <Input
                      size="small"
                      placeholder="Description (optionnel)"
                      value={item.description}
                      onChange={(e) =>
                        updateItem(item.uid, { description: e.target.value })
                      }
                      disabled={uploading}
                      className={styles.fileItemInput}
                    />
                  </>
                )}
                {item.status === "uploading" && (
                  <Progress
                    percent={item.progress}
                    size="small"
                    status="active"
                  />
                )}
                {item.status === "error" && (
                  <div className={styles.errorMsg}>{item.errorMsg}</div>
                )}
              </div>
              {(item.status === "pending" || item.status === "error") && !uploading && (
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => removeItem(item.uid)}
                  className={styles.fileItemRemove}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

PieceJointeUploadModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  parentType: PropTypes.string.isRequired,
  parentId: PropTypes.number.isRequired,
  onUploadFinish: PropTypes.func.isRequired,
};

export default PieceJointeUploadModal;
